import React, { useState, useEffect, useCallback } from 'react';
import { getWallet, initFunding, requestWithdrawal } from '../../services/walletService';
import BankAccountForm from './BankAccountForm';

const isWeekend = () => {
  const day = new Date().getDay();
  return day === 0 || day === 6;
};

const WalletPanel = () => {
  const [wallet, setWallet] = useState(null);
  const [fundAmount, setFundAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    getWallet().then(setWallet);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleFund = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const response = await initFunding(fundAmount);
      if (response.status) {
        window.location.href = response.data.authorization_url;
      } else {
        setError('Could not start funding');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start funding');
    } finally {
      setBusy(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setBusy(true);
    try {
      await requestWithdrawal(withdrawAmount);
      setMessage('Withdrawal requested. It will be processed on a business day.');
      setWithdrawAmount('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not request withdrawal');
    } finally {
      setBusy(false);
    }
  };

  if (!wallet) return <p>Loading wallet...</p>;

  const hasBankAccount = !!wallet.bankAccount;

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500">Earnings (withdrawable)</p>
          <p className="text-3xl font-bold">₦{wallet.earningsBalance.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Top-up credit (not withdrawable)</p>
          <p className="text-2xl font-semibold text-gray-600">₦{wallet.topUpBalance.toLocaleString()}</p>
        </div>

        {wallet.commissionInfo && (
          <p className="text-sm text-gray-600 sm:col-span-2 bg-accent-lighter inline-block px-3 py-1 rounded-full w-fit">
            Platform commission on venue bookings: {wallet.commissionInfo.ratePercent}%
          </p>
        )}
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      {message && <p className="text-green-600 text-sm">{message}</p>}

      <BankAccountForm existingAccount={wallet.bankAccount} onSaved={refresh} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <form onSubmit={handleFund} className="bg-white p-4 rounded-lg shadow space-y-2">
          <p className="font-semibold">Fund wallet</p>
          <p className="text-xs text-gray-500">Top-up credit only — not withdrawable.</p>
          <input type="number" min="100" value={fundAmount} onChange={(e) => setFundAmount(e.target.value)}
            placeholder="Amount (₦)" className="w-full p-2 border rounded-lg" required />
          <button type="submit" disabled={busy} className="bg-black text-white px-4 py-2 rounded-lg w-full">
            Fund via Paystack
          </button>
        </form>

        <form onSubmit={handleWithdraw} className="bg-white p-4 rounded-lg shadow space-y-2">
          <p className="font-semibold">Withdraw earnings</p>
          {!hasBankAccount && (
            <p className="text-xs text-red-500">Add and verify a payout account above first.</p>
          )}
          <input type="number" min="1000" max={wallet.earningsBalance} value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            placeholder="Amount (₦)" className="w-full p-2 border rounded-lg" required />
          <button type="submit" disabled={busy || isWeekend() || !hasBankAccount}
            className="bg-black text-white px-4 py-2 rounded-lg w-full disabled:opacity-50">
            Request withdrawal
          </button>
          {isWeekend() && (
            <p className="text-xs text-gray-500">Withdrawals are only processed Monday–Friday.</p>
          )}
        </form>
      </div>

      {wallet.pendingWithdrawals?.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="font-semibold mb-2">Pending withdrawals</p>
          <ul className="text-sm space-y-1">
            {wallet.pendingWithdrawals.map(w => (
              <li key={w._id}>₦{w.amount.toLocaleString()} — requested {new Date(w.requestedAt).toLocaleDateString()}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow">
        <p className="font-semibold mb-2">Recent activity</p>
        <ul className="text-sm divide-y">
          {wallet.history.map(h => (
            <li key={h._id} className="py-2 flex justify-between">
              <span>{h.notes || h.type}</span>
              <span className={h.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                {h.amount >= 0 ? '+' : ''}₦{h.amount.toLocaleString()}
              </span>
            </li>
          ))}
          {wallet.history.length === 0 && <li className="py-2 text-gray-500">No activity yet.</li>}
        </ul>
      </div>
    </div>
  );
};

export default WalletPanel;
